// src/pages/Dashboard.js

import React, { useState, useEffect } from 'react';
import {
    Calendar as CalendarIcon, Bell, FileText, CheckCircle,
    Clock, Activity, Plus, Trash2,
    CheckSquare, Square, X,
    Flag, Circle, ChevronLeft, ChevronRight,
    Users, Loader2, LayoutDashboard, Edit2
} from 'lucide-react';
import { toast, Toaster } from 'sonner';
import MonitoringCharts from '../components/MonitoringCharts';
import { apiFetch } from '../utils/apiFetch';
import '../App.css';
import './Dashboard.css';

const API_URL = (process.env.REACT_APP_API_URL || '') + '/api';

const EVENT_COLORS = {
    visita:         '#3b82f6',
    reuniao:        '#8b5cf6',
    procedimento:   '#059669',
    entrega:        '#f59e0b',
    administrativa: '#6366f1',
};

const EVENT_LABELS = {
    visita:         'Visita Domiciliar',
    reuniao:        'Reunião',
    procedimento:   'Procedimento',
    entrega:        'Entrega',
    administrativa: 'Administrativa',
};

const PRIORITY_COLORS = { alta: '#ef4444', media: '#f59e0b', baixa: '#10b981' };

export default function Dashboard() {
    const [user, setUser]         = useState(null);
    const [loading, setLoading]   = useState(true);

    const [currentDate, setCurrentDate]   = useState(new Date());
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [events, setEvents]             = useState([]);

    // Modal novo evento
    const [showEventModal, setShowEventModal] = useState(false);
    const [newEvent, setNewEvent] = useState({ titulo: '', hora: '09:00', tipo: 'visita', descricao: '', data: '' });

    // Modal editar evento
    const [editingEvent, setEditingEvent]           = useState(null);
    const [showEditEventModal, setShowEditEventModal] = useState(false);

    const [note, setNote]                 = useState('');
    const [noteSaving, setNoteSaving]     = useState(false);
    const [taskInput, setTaskInput]       = useState('');
    const [taskPriority, setTaskPriority] = useState('media');
    const [tasks, setTasks]               = useState([]);
    const [notifications, setNotifications] = useState([]);
    const [stats, setStats] = useState({ visitasMes: 0, eventosHoje: 0, tarefasConcluidas: 0, tarefasTotal: 0 });

    const mockPatients    = [];
    const mockChartConfig = [
        { id: 'consultas', label: 'Consultas', icon: <Activity size={16} /> },
        { id: 'exames',    label: 'Exames',    icon: <FileText size={16} /> },
    ];

    // ── Boot ──────────────────────────────────────────────────────────────────
    useEffect(() => {
        const token = localStorage.getItem('token');
        const storedUser = localStorage.getItem('user');
        
        // 🔒 Se o utilizador entrar na aba já deslogado, expulsa para o login na hora!
        if (!token || !storedUser) { 
            window.location.href = '/login?session_expired=true'; 
            return; 
        }
        try {
            const userData = JSON.parse(storedUser);
            setUser(userData);
            loadDashboardData(userData.id); // O apiFetch cuida do token lá dentro!
        } catch { toast.error('Erro ao carregar dados do usuário'); setLoading(false); }
    }, []);

    // Autosave nota
    useEffect(() => {
        if (!user?.id || loading || !note) return;
        const tid = setTimeout(() => saveNote(), 1000);
        return () => clearTimeout(tid);
    }, [note, user?.id]);

    // ── Carregamento ──────────────────────────────────────────────────────────
    const loadDashboardData = async (userId) => {
        setLoading(true);
        try {
            await Promise.all([loadNotes(userId), loadEvents(userId), loadTasks(userId), loadNotifications(userId), loadStats(userId)]);
        } catch { toast.error('Erro ao carregar dados do dashboard'); }
        finally { setLoading(false); }
    };

    const loadNotes = async (userId) => {
        try {
            const r = await apiFetch(`/api/dashboard/notas/${userId}`);
            const d = await r.json();
            setNote(d.conteudo || '');
        } catch { console.error('Erro ao carregar notas'); }
    };

    const saveNote = async () => {
        if (!user?.id || noteSaving) return;
        setNoteSaving(true);
        try {
            await await apiFetch(`/api/dashboard/notas/${user.id}`, {
                method: 'POST', body: JSON.stringify({ conteudo: note })
            });
        } catch { console.error('Erro ao salvar nota'); }
        finally { setNoteSaving(false); }
    };

    // ── Eventos ───────────────────────────────────────────────────────────────
    const loadEvents = async (userId) => {
        try {
            const r = await apiFetch(`/api/dashboard/eventos/${userId}`);
            const d = await r.json();
            setEvents(Array.isArray(d) ? d : []);
        } catch { setEvents([]); }
    };

    const addEvent = async () => {
        if (!newEvent.titulo.trim()) { toast.error('Digite um título para o evento'); return; }
        const dataEvento = newEvent.data || selectedDate.toISOString().split('T')[0];
        const eventData  = {
            usuario_id: user.id, titulo: newEvent.titulo, data: dataEvento,
            hora: newEvent.hora, tipo: newEvent.tipo,
            descricao: newEvent.descricao || null,
        };
        try {
            const r      = await apiFetch(`/api/dashboard/eventos`, { method: 'POST', body: JSON.stringify(eventData) });
            const result = await r.json();
            if (result.success) {
                await loadEvents(user.id); await loadStats(user.id);
                const d = new Date(dataEvento + 'T00:00:00');
                setCurrentDate(new Date(d.getFullYear(), d.getMonth(), 1));
                setSelectedDate(d);
                setNewEvent({ titulo: '', hora: '09:00', tipo: 'visita', descricao: '', data: '' });
                setShowEventModal(false);
                toast.success('Evento adicionado!');
            }
        } catch { toast.error('Erro ao criar evento'); }
    };

    const openEditEvent = (event) => { setEditingEvent({ ...event }); setShowEditEventModal(true); };

    const saveEditEvent = async () => {
        if (!editingEvent.titulo.trim()) { toast.error('Título obrigatório'); return; }
        try {
            const r = await apiFetch(`/api/dashboard/eventos/${editingEvent.id}`, {
                method: 'PUT',
                body: JSON.stringify({
                    titulo: editingEvent.titulo, data: editingEvent.data,
                    hora: editingEvent.hora, tipo: editingEvent.tipo,
                    descricao: editingEvent.descricao || null,
                })
            });
            const result = await r.json();
            if (result.success) {
                await loadEvents(user.id); await loadStats(user.id);
                setShowEditEventModal(false); setEditingEvent(null);
                toast.success('Evento atualizado!');
            } else { toast.error('Erro ao salvar evento'); }
        } catch { toast.error('Erro ao salvar evento'); }
    };

    const deleteEvent = async (eventoId) => {
        try {
            const r      = await apiFetch(`/api/dashboard/eventos/${eventoId}`, { method: 'DELETE' });
            const result = await r.json();
            if (result.success) { await loadEvents(user.id); await loadStats(user.id); toast.info('Evento removido'); }
        } catch { toast.error('Erro ao deletar evento'); }
    };

    // ── Tarefas ───────────────────────────────────────────────────────────────
    const loadTasks = async (userId) => {
        try {
            const r    = await apiFetch(`/api/dashboard/tarefas/${userId}`);
            const data = await r.json();
            if (Array.isArray(data)) {
                setTasks(data.map(t => ({
                    ...t,
                    done:      t.concluida === 1,
                    priority:  t.prioridade || 'media',   // ← lê prioridade real do DB
                    text:      t.texto,
                    createdAt: new Date(t.data_criacao).getTime(),
                })));
            } else setTasks([]);
        } catch { setTasks([]); }
    };

    const addTask = async () => {
        if (!taskInput.trim()) return;
        try {
            const r      = await apiFetch(`/api/dashboard/tarefas`, {
                method: 'POST',
                body: JSON.stringify({ usuario_id: user.id, texto: taskInput, prioridade: taskPriority })  // ← salva prioridade
            });
            const result = await r.json();
            if (result.success) { await loadTasks(user.id); await loadStats(user.id); setTaskInput(''); toast.success('Tarefa adicionada!'); }
        } catch { toast.error('Erro ao adicionar tarefa'); }
    };

    const toggleTask = async (tarefaId, currentState) => {
        try {
            const r      = await apiFetch(`/api/dashboard/tarefas/${tarefaId}`, { method: 'PUT', body: JSON.stringify({ concluida: currentState ? 0 : 1 }) });
            const result = await r.json();
            if (result.success) { await loadTasks(user.id); await loadStats(user.id); toast.success(currentState ? 'Tarefa reaberta' : 'Tarefa concluída! 🎉'); }
        } catch { toast.error('Erro ao atualizar tarefa'); }
    };

    const deleteTask = async (tarefaId) => {
        try {
            const r      = await apiFetch(`/api/dashboard/tarefas/${tarefaId}`, { method: 'DELETE' });
            const result = await r.json();
            if (result.success) { await loadTasks(user.id); await loadStats(user.id); toast.info('Tarefa removida'); }
        } catch { toast.error('Erro ao deletar tarefa'); }
    };

    // ── Notificações ──────────────────────────────────────────────────────────
    const loadNotifications = async (userId) => {
        try {
            const r    = await apiFetch(`/api/dashboard/notificacoes/${userId}`);
            const data = await r.json();
            if (Array.isArray(data)) {
                setNotifications(data.map(n => ({
                    ...n,
                    read:  n.lida === 1,
                    texto: n.mensagem || n.texto || '',   // ← fix: DB usa coluna 'mensagem'
                })));
            } else setNotifications([]);
        } catch { setNotifications([]); }
    };

    const markAllRead = async () => {
        try {
            await apiFetch(`/api/dashboard/notificacoes/${user.id}/lida`, { method: 'PUT' });
            await loadNotifications(user.id);
        } catch { console.error('Erro ao marcar notificações'); }
    };

    const deleteNotification = async (notifId) => {
        try {
            await apiFetch(`/api/dashboard/notificacoes/${user.id}/${notifId}`, { method: 'DELETE'});
            await loadNotifications(user.id);
        } catch { console.error('Erro ao deletar notificação'); }
    };

    // ── Stats ─────────────────────────────────────────────────────────────────
    const loadStats = async (userId) => {
        try {
            const r    = await apiFetch(`/api/dashboard/stats/${userId}`);
            const data = await r.json();
            setStats({
                visitasMes:        data.eventosMes        ?? 0,
                eventosHoje:       data.eventosHoje       ?? 0,
                tarefasConcluidas: data.tarefasConcluidas ?? 0,
                tarefasTotal:      data.tarefasTotal      ?? 0,
            });
        } catch { console.error('Erro ao carregar estatísticas'); }
    };

    // ── Calendário ────────────────────────────────────────────────────────────
    const getDaysInMonth = (date) => {
        const year = date.getFullYear(), month = date.getMonth();
        const firstDay = new Date(year, month, 1), lastDay = new Date(year, month + 1, 0);
        const days = [];
        for (let i = 0; i < firstDay.getDay(); i++) days.push({ date: null, isCurrentMonth: false });
        for (let i = 1; i <= lastDay.getDate(); i++) days.push({ date: new Date(year, month, i), isCurrentMonth: true });
        return days;
    };

    const isToday         = (d)      => d && d.toDateString() === new Date().toDateString();
    const isSameDay       = (d1, d2) => d1 && d2 && d1.toDateString() === d2.toDateString();
    const hasEvents       = (date)   => date && Array.isArray(events) && events.some(e => e.data === date.toISOString().split('T')[0]);
    const getEventsForDate = (date)  => {
        if (!date || !Array.isArray(events)) return [];
        return events.filter(e => e.data === date.toISOString().split('T')[0]).sort((a, b) => a.hora.localeCompare(b.hora));
    };

    const nextMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
    const prevMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));

    const openNewEventModal = () => {
        setNewEvent({ titulo: '', hora: '09:00', tipo: 'visita', descricao: '', data: selectedDate.toISOString().split('T')[0] });
        setShowEventModal(true);
    };

    // ── Guards ────────────────────────────────────────────────────────────────
    if (!user) return <div className="dashboard-container"><div className="loading-container"><Loader2 size={44} className="spinner" /><p>Autenticando...</p></div></div>;
    if (loading) return <div className="dashboard-container"><div className="loading-container"><Loader2 size={44} className="spinner" /><p>Carregando dashboard...</p></div></div>;

    const monthName          = currentDate.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
    const todayStr           = new Date().toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' });
    const selectedDateEvents = getEventsForDate(selectedDate);
    const unreadCount        = notifications.filter(n => !n.read).length;

    return (
        <div className="dashboard-container">
            <Toaster position="top-right" richColors />

            {/* ─── HEADER ─────────── */}
            <div className="dashboard-page-header">
                <div className="dashboard-page-header-inner">
                    <div>
                        <h1>
                            <div className="header-icon-box"><LayoutDashboard size={18} color="white" /></div>
                            Olá, {user.nome || user.name} 👋
                        </h1>
                        <p className="dashboard-subtitle">Aqui está o resumo do seu dia.</p>
                    </div>
                    <div className="date-display">
                        <CalendarIcon size={16} />
                        <span>{todayStr}</span>
                    </div>
                </div>
            </div>

            <div className="dashboard-content">

                {/* STATS */}
                <div className="stats-widget-container">
                    <div className="stat-card" style={{ '--card-color': '#059669' }}>
                        <div className="stat-header"><div className="stat-icon" style={{ background: '#d1fae5' }}><Users size={22} color="#059669" /></div></div>
                        <div className="stat-body"><div className="stat-value">{stats.visitasMes}</div><div className="stat-label">Eventos do Mês</div></div>
                    </div>
                    <div className="stat-card" style={{ '--card-color': '#8b5cf6' }}>
                        <div className="stat-header"><div className="stat-icon" style={{ background: '#ede9fe' }}><CalendarIcon size={22} color="#8b5cf6" /></div></div>
                        <div className="stat-body"><div className="stat-value">{stats.eventosHoje}</div><div className="stat-label">Eventos Hoje</div></div>
                    </div>
                    <div className="stat-card" style={{ '--card-color': '#0369a1' }}>
                        <div className="stat-header"><div className="stat-icon" style={{ background: '#e0f2fe' }}><CheckCircle size={22} color="#0369a1" /></div></div>
                        <div className="stat-body">
                            <div className="stat-value">{stats.tarefasConcluidas}<span className="stat-total">/{stats.tarefasTotal}</span></div>
                            <div className="stat-label">Tarefas Concluídas</div>
                        </div>
                        {stats.tarefasTotal > 0 && (
                            <div className="stat-progress">
                                <div className="progress-bar"><div className="progress-fill" style={{ width: `${(stats.tarefasConcluidas / stats.tarefasTotal) * 100}%`, background: '#0369a1' }} /></div>
                                <span className="progress-text">{Math.round((stats.tarefasConcluidas / stats.tarefasTotal) * 100)}%</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* ATALHOS */}
                <div className="quick-actions">
                    <button className="action-btn" onClick={openNewEventModal}><CalendarIcon size={18} /> Novo Evento</button>
                </div>

                {/* GRID */}
                <div className="dashboard-grid">

                    {/* COLUNA 1 */}
                    <div className="grid-column">

                        {/* CALENDÁRIO */}
                        <div className="dashboard-widget calendar-widget">
                            <div className="widget-header"><h2><CalendarIcon size={18} /> Calendário</h2></div>
                            <div className="calendar-container">
                                <div className="calendar-nav">
                                    <button onClick={prevMonth} className="nav-btn"><ChevronLeft size={18} /></button>
                                    <h3>{monthName}</h3>
                                    <button onClick={nextMonth} className="nav-btn"><ChevronRight size={18} /></button>
                                </div>
                                <div className="calendar-weekdays">
                                    {['Dom','Seg','Ter','Qua','Qui','Sex','Sáb'].map(d => <div key={d} className="weekday">{d}</div>)}
                                </div>
                                <div className="calendar-grid">
                                    {getDaysInMonth(currentDate).map((day, idx) => (
                                        <div key={idx}
                                            className={`calendar-day ${!day.isCurrentMonth ? 'other-month' : ''} ${isToday(day.date) ? 'today' : ''} ${isSameDay(day.date, selectedDate) ? 'selected' : ''} ${hasEvents(day.date) ? 'has-events' : ''}`}
                                            onClick={() => day.date && setSelectedDate(day.date)}>
                                            {day.date && (
                                                <>
                                                    <span className="day-number">{day.date.getDate()}</span>
                                                    {hasEvents(day.date) && (
                                                        <div className="event-indicators">
                                                            {getEventsForDate(day.date).slice(0, 3).map(e => (
                                                                <Circle key={e.id} size={5} fill={e.cor || '#059669'} color={e.cor || '#059669'} />
                                                            ))}
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* EVENTOS DO DIA */}
                        <div className="dashboard-widget">
                            <div className="widget-header">
                                <h2>
                                    <Clock size={18} />
                                    {isSameDay(selectedDate, new Date())
                                        ? 'Hoje'
                                        : selectedDate.toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'short' })}
                                </h2>
                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                    <span className="badge">{selectedDateEvents.length}</span>
                                    <button className="nav-btn" onClick={openNewEventModal} title="Adicionar evento" style={{ width: 30, height: 30 }}>
                                        <Plus size={14} />
                                    </button>
                                </div>
                            </div>
                            <div className="events-list">
                                {selectedDateEvents.length === 0
                                    ? <p className="empty-state">Nenhum evento agendado</p>
                                    : selectedDateEvents.map(event => (
                                        <div key={event.id} className="event-item" onClick={() => openEditEvent(event)}>
                                            <div className="event-color" style={{ background: event.cor || EVENT_COLORS[event.tipo] || '#059669' }} />
                                            <div className="time-box">{event.hora}</div>
                                            <div className="info-box">
                                                <strong>{event.titulo}</strong>
                                                <span className="event-type">
                                                    {EVENT_LABELS[event.tipo] || event.tipo}
                                                    {event.descricao && <span style={{ marginLeft: 4, opacity: 0.6 }}>· 📝</span>}
                                                </span>
                                            </div>
                                            <button className="edit-btn"
                                                onClick={(e) => { e.stopPropagation(); openEditEvent(event); }}
                                                title="Editar evento">
                                                <Edit2 size={13} />
                                            </button>
                                            <button className="delete-btn"
                                                onClick={(e) => { e.stopPropagation(); deleteEvent(event.id); }}
                                                title="Excluir">
                                                <X size={15} />
                                            </button>
                                        </div>
                                    ))}
                            </div>
                        </div>

                        {mockPatients.length > 0 && <MonitoringCharts patients={mockPatients} chartConfig={mockChartConfig} />}
                    </div>

                    {/* COLUNA 2 */}
                    <div className="grid-column">

                        {/* NOTIFICAÇÕES */}
                        <div className="dashboard-widget">
                            <div className="widget-header">
                                <h2><Bell size={18} /> Avisos</h2>
                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                    {unreadCount > 0 && (
                                        <button className="mark-read-btn" onClick={markAllRead}>Marcar lidas</button>
                                    )}
                                    <span className="badge">{unreadCount}</span>
                                </div>
                            </div>
                            <div className="notification-list">
                                {notifications.length === 0
                                    ? <p className="empty-state">Nenhuma notificação</p>
                                    : notifications.map(notif => (
                                        <div key={notif.id}
                                            className={`notification-item ${notif.prioridade || ''} ${notif.read ? 'read' : ''}`}
                                            onClick={markAllRead}>
                                            <div className="bullet" />
                                            <p>{notif.texto}</p>
                                            <button onClick={(e) => { e.stopPropagation(); deleteNotification(notif.id); }} className="delete-btn">
                                                <X size={13} />
                                            </button>
                                        </div>
                                    ))}
                            </div>
                        </div>

                        {/* TAREFAS */}
                        <div className="dashboard-widget todo-widget">
                            <div className="widget-header">
                                <h2><CheckSquare size={18} /> Demandas Rápidas</h2>
                                <span className="badge">{tasks.filter(t => !t.done).length}</span>
                            </div>
                            <div className="todo-input-area">
                                <input
                                    type="text" placeholder="Adicionar nova tarefa..."
                                    value={taskInput}
                                    onChange={(e) => setTaskInput(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && addTask()}
                                />
                                <select
                                    value={taskPriority}
                                    onChange={(e) => setTaskPriority(e.target.value)}
                                    className={`priority-select priority-select-${taskPriority}`}>
                                    <option value="alta">🔴 Alta</option>
                                    <option value="media">🟡 Média</option>
                                    <option value="baixa">🟢 Baixa</option>
                                </select>
                                <button onClick={addTask}><Plus size={17} /></button>
                            </div>
                            <div className="todo-list">
                                {tasks.length === 0 && <p className="empty-state">Nenhuma tarefa pendente.</p>}
                                {tasks
                                    .sort((a, b) => {
                                        if (a.done !== b.done) return a.done ? 1 : -1;
                                        const o = { alta: 0, media: 1, baixa: 2 };
                                        return (o[a.priority] ?? 1) - (o[b.priority] ?? 1);
                                    })
                                    .map(task => (
                                        <div key={task.id} className={`todo-item priority-border-${task.priority} ${task.done ? 'done' : ''}`}>
                                            <div className="check-area" onClick={() => toggleTask(task.id, task.done)}>
                                                {task.done ? <CheckSquare size={19} color="#059669" /> : <Square size={19} color="#94a3b8" />}
                                                <div className="task-content">
                                                    <span>{task.text}</span>
                                                    <Flag size={12} fill={PRIORITY_COLORS[task.priority]} color={PRIORITY_COLORS[task.priority]} className="priority-flag" />
                                                </div>
                                            </div>
                                            <button onClick={() => deleteTask(task.id)} className="delete-btn"><Trash2 size={15} /></button>
                                        </div>
                                    ))}
                            </div>
                        </div>

                        {/* NOTAS */}
                        <div className="dashboard-widget notepad-widget">
                            <div className="widget-header">
                                <h2><FileText size={18} /> Bloco de Notas</h2>
                                <span className="save-badge">{noteSaving ? 'Salvando...' : '✓ Salvo'}</span>
                            </div>
                            <textarea
                                className="notepad-area"
                                placeholder="Digite anotações rápidas aqui..."
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* ─── MODAL NOVO EVENTO ───────────────────────── */}
            {showEventModal && (
                <div className="modal-overlay" onClick={() => setShowEventModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Novo Evento</h3>
                            <button onClick={() => setShowEventModal(false)} className="close-btn"><X size={18} /></button>
                        </div>
                        <div className="modal-body">
                            <div className="form-group">
                                <label>Título</label>
                                <input type="text" value={newEvent.titulo} autoFocus
                                    onChange={(e) => setNewEvent({ ...newEvent, titulo: e.target.value })}
                                    placeholder="Ex: Visita - Maria Silva" />
                            </div>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Data</label>
                                    <input type="date" value={newEvent.data}
                                        onChange={(e) => setNewEvent({ ...newEvent, data: e.target.value })} />
                                </div>
                                <div className="form-group">
                                    <label>Horário</label>
                                    <input type="time" value={newEvent.hora}
                                        onChange={(e) => setNewEvent({ ...newEvent, hora: e.target.value })} />
                                </div>
                            </div>
                            <div className="form-group">
                                <label>Tipo</label>
                                <select value={newEvent.tipo} onChange={(e) => setNewEvent({ ...newEvent, tipo: e.target.value })}>
                                    {Object.entries(EVENT_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Descrição (opcional)</label>
                                <textarea value={newEvent.descricao}
                                    onChange={(e) => setNewEvent({ ...newEvent, descricao: e.target.value })}
                                    placeholder="Detalhes, endereço, observações..." rows="3" />
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button onClick={() => setShowEventModal(false)} className="btn-secondary">Cancelar</button>
                            <button onClick={addEvent} className="btn-primary">Adicionar Evento</button>
                        </div>
                    </div>
                </div>
            )}

            {/* ─── MODAL EDITAR EVENTO ─────────────────────── */}
            {showEditEventModal && editingEvent && (
                <div className="modal-overlay" onClick={() => setShowEditEventModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <div className="event-modal-dot" style={{ background: EVENT_COLORS[editingEvent.tipo] || '#059669' }} />
                                <h3>Editar Evento</h3>
                            </div>
                            <button onClick={() => setShowEditEventModal(false)} className="close-btn"><X size={18} /></button>
                        </div>
                        <div className="modal-body">
                            <div className="form-group">
                                <label>Título</label>
                                <input type="text" value={editingEvent.titulo} autoFocus
                                    onChange={(e) => setEditingEvent({ ...editingEvent, titulo: e.target.value })} />
                            </div>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Data</label>
                                    <input type="date" value={editingEvent.data}
                                        onChange={(e) => setEditingEvent({ ...editingEvent, data: e.target.value })} />
                                </div>
                                <div className="form-group">
                                    <label>Horário</label>
                                    <input type="time" value={editingEvent.hora}
                                        onChange={(e) => setEditingEvent({ ...editingEvent, hora: e.target.value })} />
                                </div>
                            </div>
                            <div className="form-group">
                                <label>Tipo</label>
                                <select value={editingEvent.tipo} onChange={(e) => setEditingEvent({ ...editingEvent, tipo: e.target.value })}>
                                    {Object.entries(EVENT_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Descrição</label>
                                <textarea value={editingEvent.descricao || ''}
                                    onChange={(e) => setEditingEvent({ ...editingEvent, descricao: e.target.value })}
                                    placeholder="Detalhes, endereço, observações..." rows="3" />
                            </div>
                        </div>
                        <div className="modal-footer" style={{ justifyContent: 'space-between' }}>
                            <button className="btn-danger" onClick={() => { deleteEvent(editingEvent.id); setShowEditEventModal(false); }}>
                                <Trash2 size={15} /> Excluir
                            </button>
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <button onClick={() => setShowEditEventModal(false)} className="btn-secondary">Cancelar</button>
                                <button onClick={saveEditEvent} className="btn-primary">Salvar Alterações</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}